import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import type {
  WSTerminalMessage,
  TerminalStatus,
  TerminalResize,
  TerminalStartData,
} from '@/types/terminal';

interface UseTerminalOptions {
  agentId: string;
  enabled?: boolean;
  onData?: (data: string | Uint8Array) => void;
  onStatusChange?: (status: TerminalStatus) => void;
  onError?: (error: string) => void;
}

interface ReconnectConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  connectionTimeoutMs: number;
}

const DEFAULT_RECONNECT_CONFIG: ReconnectConfig = {
  maxRetries: 10,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  connectionTimeoutMs: 10000,
};

export const useTerminal = ({
  agentId,
  enabled = true,
  onData,
  onStatusChange,
  onError,
}: UseTerminalOptions) => {
  const wsRef = useRef<WebSocket | null>(null);
  const connectDelayRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const lastCloseCodeRef = useRef<number | null>(null);
  // Pending terminal size — updated by resize() before/after WS open
  const pendingSizeRef = useRef<TerminalStartData>({ cols: 80, rows: 24 });
  // Stable refs to break circular dependencies between connect/disconnect
  const connectRef = useRef<() => void>(() => {});
  const disconnectRef = useRef<() => void>(() => {});
  // Prevents reconnect when the disconnect was intentional (user action or terminal.stop command)
  const intentionalCloseRef = useRef(false);

  const [status, setStatus] = useState<TerminalStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);

  const updateStatus = useCallback(
    (newStatus: TerminalStatus) => {
      setStatus(newStatus);
      onStatusChange?.(newStatus);
    },
    [onStatusChange]
  );

  const handleError = useCallback(
    (errorMsg: string) => {
      setError(errorMsg);
      onError?.(errorMsg);
    },
    [onError]
  );

  const getBackoffDelay = useCallback((attemptNumber: number): number => {
    const baseDelay = DEFAULT_RECONNECT_CONFIG.initialDelayMs;
    const maxDelay = DEFAULT_RECONNECT_CONFIG.maxDelayMs;
    const delay = baseDelay * Math.pow(2, attemptNumber - 1);
    return Math.min(delay, maxDelay);
  }, []);

  // Use || 100 so NaN (when env var is unset) falls back to 100ms correctly
  const CONNECT_DELAY_MS = Number(import.meta.env.VITE_WS_CONNECT_DELAY_MS) || 100;

  const logTerminalWsState = useCallback(
    (message: string, details?: Record<string, any>) => {
      const readyState = wsRef.current?.readyState ?? 'null';
      const readyStateLabel =
        readyState === 0
          ? 'CONNECTING'
          : readyState === 1
            ? 'OPEN'
            : readyState === 2
              ? 'CLOSING'
              : readyState === 3
                ? 'CLOSED'
                : 'UNKNOWN';
      console.log(`[TERMINAL WS] ${message}`, {
        readyState: `${readyState}(${readyStateLabel})`,
        attempts: reconnectAttemptsRef.current,
        lastCloseCode: lastCloseCodeRef.current,
        agentId,
        ...details,
      });
    },
    [agentId]
  );

  const decodeBase64ToBytes = useCallback((value: string): Uint8Array => {
    // Accept both standard base64 and URL-safe base64 variants.
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }, []);

  // Sends terminal.start with current pending size. Called on WS open and on manual restart.
  const sendStart = useCallback(
    (ws: WebSocket) => {
      // Reutiliza la contraseña validada en la sesión de pantalla (gate opcional).
      let password: string | undefined;
      try {
        password = sessionStorage.getItem(`ra_conn_pw_${agentId}`) || undefined;
      } catch (_e) {
        /* ignore */
      }
      const startMessage: WSTerminalMessage = {
        type: 'terminal.start',
        agentId,
        data: { ...pendingSizeRef.current, ...(password ? { password } : {}) },
      };
      ws.send(JSON.stringify(startMessage));
      logTerminalWsState('Sent terminal.start', pendingSizeRef.current);
    },
    [agentId, logTerminalWsState]
  );

  const connect = useCallback(() => {
    if (!enabled) {
      logTerminalWsState('Skipping connect: terminal disabled');
      return;
    }

    if (!isMountedRef.current) {
      logTerminalWsState('Skipping connect: component not mounted');
      return;
    }

    if (isConnectingRef.current) {
      logTerminalWsState('Skipping connect: connection already in progress');
      return;
    }

    if (reconnectAttemptsRef.current > DEFAULT_RECONNECT_CONFIG.maxRetries) {
      logTerminalWsState('Max retries exceeded', {
        maxRetries: DEFAULT_RECONNECT_CONFIG.maxRetries,
      });
      updateStatus('error');
      handleError('Max connection retries exceeded');
      return;
    }

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    isConnectingRef.current = true;
    intentionalCloseRef.current = false;
    reconnectAttemptsRef.current += 1;
    updateStatus('connecting');
    setError(null);

    const apiBase =
      import.meta.env.VITE_API_URL || `${window.location.protocol}//${window.location.host}/api/v1`;
    const wsEnv = import.meta.env.VITE_WS_URL as string | undefined;
    const wsBaseRaw = wsEnv && wsEnv.trim().length > 0 ? wsEnv.trim() : apiBase;
    const wsBase = wsBaseRaw.replace(/^http/i, 'ws').replace(/\/$/, '');
    const wsUrl = wsBase.endsWith('/api/v1')
      ? `${wsBase}/terminal/${agentId}/ws`
      : `${wsBase}/api/v1/terminal/${agentId}/ws`;

    logTerminalWsState(
      `Connecting (attempt ${reconnectAttemptsRef.current}/${DEFAULT_RECONNECT_CONFIG.maxRetries})`,
      { wsUrl }
    );

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    connectionTimeoutRef.current = setTimeout(() => {
      if (
        isMountedRef.current &&
        isConnectingRef.current &&
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        logTerminalWsState('Connection timeout exceeded, closing');
        try {
          wsRef.current?.close();
        } catch {}
      }
    }, DEFAULT_RECONNECT_CONFIG.connectionTimeoutMs);

    ws.onopen = () => {
      // Abandoned socket: wsRef was replaced while this socket was still connecting
      if (wsRef.current !== ws) {
        logTerminalWsState('Abandoned socket connected — closing it');
        try {
          ws.close();
        } catch {}
        return;
      }
      if (!isMountedRef.current) return;

      isConnectingRef.current = false;
      reconnectAttemptsRef.current = 0;
      lastCloseCodeRef.current = null;

      logTerminalWsState('Connected successfully');
      updateStatus('connected');

      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      sendStart(ws);
    };

    ws.onclose = (event) => {
      // Abandoned socket closed — ignore completely
      if (wsRef.current !== ws && wsRef.current !== null) {
        logTerminalWsState('Abandoned socket closed — ignoring');
        return;
      }
      if (!isMountedRef.current) return;

      isConnectingRef.current = false;
      lastCloseCodeRef.current = event.code;

      logTerminalWsState(`Closed`, {
        code: event.code,
        reason: event.reason,
        intentional: intentionalCloseRef.current,
      });

      wsRef.current = null;
      updateStatus('disconnected');

      // Skip reconnect when the close was intentional or a policy violation
      if (intentionalCloseRef.current || event.code === 1008) {
        intentionalCloseRef.current = false;
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        return;
      }

      if (
        isMountedRef.current &&
        reconnectAttemptsRef.current <= DEFAULT_RECONNECT_CONFIG.maxRetries
      ) {
        const nextDelay = getBackoffDelay(reconnectAttemptsRef.current);
        logTerminalWsState(`Scheduling reconnect`, {
          delayMs: nextDelay,
          attempt: reconnectAttemptsRef.current,
        });

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectRef.current();
          }
        }, nextDelay) as unknown as number;
      }

      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };

    ws.onerror = (ev) => {
      if (!isMountedRef.current) return;

      logTerminalWsState('WebSocket error', { errorType: ev.type });
      handleError('WebSocket connection error');
      updateStatus('error');
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current) return;

      try {
        // Parse as generic object first — adminHub broadcasts non-terminal messages too
        const raw = JSON.parse(event.data) as {
          type: string;
          data?: unknown;
          agentId?: string;
          error?: string;
        };
        const msgType = raw.type;
        const msgData =
          raw.data && typeof raw.data === 'object'
            ? (raw.data as Record<string, unknown>)
            : ({} as Record<string, unknown>);

        switch (msgType) {
          case 'terminal.init':
            logTerminalWsState('Relay ready');
            break;

          case 'terminal.data': {
            // Filter out data for other agents (adminHub broadcasts to all connections)
            if (msgData.agentId && msgData.agentId !== agentId) {
              logTerminalWsState('terminal.data filtered (different agentId)', {
                msgAgentId: msgData.agentId,
                myAgentId: agentId,
              });
              break;
            }
            // Support both current and legacy payload shapes.
            const b64 =
              (msgData.data as string | undefined) ??
              (typeof raw.data === 'string' ? raw.data : undefined);
            const rawText =
              (msgData.text as string | undefined) ?? (msgData.output as string | undefined);

            if (b64 && b64.length > 0) {
              logTerminalWsState('terminal.data received', { bytes: b64.length });
              try {
                onData?.(decodeBase64ToBytes(b64));
              } catch (e) {
                logTerminalWsState('terminal.data decode error', { error: String(e) });
                onData?.(b64);
              }
            } else if (rawText) {
              logTerminalWsState('terminal.data legacy text payload received', {
                length: rawText.length,
              });
              onData?.(rawText);
            }
            break;
          }

          case 'terminal.started':
            logTerminalWsState('Terminal started');
            updateStatus('connected');
            setError(null);
            break;

          case 'terminal.stopped': {
            // Shell exited OR agent disconnected. Keep the WS open so the user can restart
            // without a full reconnect. Show 'terminated' state and let TerminalPage offer restart.
            const reason = (msgData.reason as string | undefined) ?? 'shell_exited';
            logTerminalWsState(`Terminal stopped (${reason})`);
            updateStatus('terminated');
            break;
          }

          case 'terminal.error': {
            const errMsg =
              (msgData.error as string | undefined) ??
              (raw.error as string | undefined) ??
              'Unknown terminal error';
            logTerminalWsState('Terminal error from server', { error: errMsg });
            handleError(errMsg);
            updateStatus('error');
            break;
          }

          case 'agent.disconnected': {
            // The agent's control WS dropped — all terminal sessions are gone.
            if (msgData.agentId === agentId) {
              logTerminalWsState('Agent disconnected — terminal session lost');
              updateStatus('terminated');
            }
            break;
          }
        }
      } catch (err) {
        console.error('[TERMINAL WS] Failed to parse message:', err);
      }
    };
  }, [
    agentId,
    enabled,
    onData,
    updateStatus,
    handleError,
    logTerminalWsState,
    getBackoffDelay,
    sendStart,
    decodeBase64ToBytes,
  ]);

  const disconnect = useCallback(() => {
    logTerminalWsState('Disconnecting (intentional)');

    intentionalCloseRef.current = true;
    isConnectingRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (wsRef.current) {
      const readyState = wsRef.current.readyState;

      if (readyState === WebSocket.OPEN) {
        try {
          wsRef.current.close(1000, 'Normal disconnect');
        } catch {}
      } else if (readyState === WebSocket.CONNECTING) {
        // Abandon: let the socket finish connecting but our onopen guard will close it
        logTerminalWsState('Abandoning CONNECTING socket');
      }

      wsRef.current = null;
    }

    if (connectDelayRef.current) {
      clearTimeout(connectDelayRef.current);
      connectDelayRef.current = null;
    }

    updateStatus('disconnected');
  }, [updateStatus, logTerminalWsState]);

  // Restart the shell session without reconnecting the WS.
  // Safe to call when WS is open and status is 'terminated' or 'connected'.
  const restartShell = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      logTerminalWsState('restartShell: WS not open, reconnecting instead');
      disconnectRef.current();
      setTimeout(() => {
        if (isMountedRef.current) connectRef.current();
      }, 200);
      return;
    }
    logTerminalWsState('Restarting shell (resending terminal.start)');
    updateStatus('connecting');
    setError(null);
    sendStart(wsRef.current);
  }, [logTerminalWsState, updateStatus, sendStart]);

  const sendData = useCallback(
    (data: string) => {
      if (!isMountedRef.current) {
        logTerminalWsState('Skipping sendData: component not mounted');
        return;
      }

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // Encode as UTF-8 bytes then base64 to safely transport any Unicode input.
        const utf8 = new TextEncoder().encode(data);
        let binary = '';
        for (const byte of utf8) binary += String.fromCharCode(byte);
        const encoded = btoa(binary);
        const message: WSTerminalMessage = {
          type: 'terminal.data',
          agentId,
          data: { data: encoded },
        };
        wsRef.current.send(JSON.stringify(message));
      } else {
        logTerminalWsState('Skipping sendData: WebSocket not OPEN', {
          readyState: wsRef.current?.readyState,
        });
      }
    },
    [agentId, logTerminalWsState]
  );

  const resize = useCallback(
    (cols: number, rows: number) => {
      pendingSizeRef.current = { cols, rows };

      if (!isMountedRef.current) {
        logTerminalWsState('Skipping resize: component not mounted');
        return;
      }

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const resizeData: TerminalResize = { cols, rows };
        const message: WSTerminalMessage = {
          type: 'terminal.resize',
          agentId,
          data: resizeData,
        };
        wsRef.current.send(JSON.stringify(message));
      } else {
        logTerminalWsState('Size queued for terminal.start', { cols, rows });
      }
    },
    [agentId, logTerminalWsState]
  );

  useLayoutEffect(() => {
    connectRef.current = connect;
    disconnectRef.current = disconnect;
  });

  useEffect(() => {
    isMountedRef.current = true;
    reconnectAttemptsRef.current = 0;

    logTerminalWsState(enabled ? 'Mount' : 'Mount (disabled)');
    if (!enabled) {
      // Don't connect when disabled — cleanup only runs on unmount.
      // Avoid calling disconnect() (which setState) directly in the effect body
      // to satisfy react-hooks/set-state-in-effect.
      return () => {
        logTerminalWsState('Unmount (disabled)');
        isMountedRef.current = false;
        disconnect();
      };
    }

    connectDelayRef.current = window.setTimeout(() => {
      if (isMountedRef.current) connect();
      connectDelayRef.current = null;
    }, CONNECT_DELAY_MS) as unknown as number;

    return () => {
      logTerminalWsState('Unmount');
      isMountedRef.current = false;
      if (connectDelayRef.current) {
        clearTimeout(connectDelayRef.current);
        connectDelayRef.current = null;
      }
      disconnect();
    };
  }, [connect, disconnect, enabled, logTerminalWsState, CONNECT_DELAY_MS]);

  return {
    status,
    error,
    connect,
    disconnect,
    restartShell,
    sendData,
    resize,
  };
};
